import { Alert, Button, Stack, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ConflictOfInterestAck } from "../../components/ConflictOfInterestAck";
import { LoadingOverlay, PageHeader, ContentCard } from "../../components/ui";
import { hrTerms } from "../../constants/platformTerms";
import { HR_KYC_COPY, hrNeedsIdentityKyc } from "../../lib/companyKyc";
import { companyApi } from "../../lib/api";
import { NAV_ICONS } from "../../lib/navCopy";
import { ApiError } from "../../lib/apiClient";
import { kycStatusLabel } from "../../lib/kyc";

export function HrIdentityKycPage() {
  const queryClient = useQueryClient();
  const kycQuery = useQuery({ queryKey: ["hr-kyc"], queryFn: companyApi.hrKyc });
  const [designationOverride, setDesignationOverride] = useState<string | null>(null);
  const [personalPan, setPersonalPan] = useState("");
  const [conflictAccepted, setConflictAccepted] = useState(false);

  const kyc = kycQuery.data;
  const conflictCopy = hrTerms.conflictOfInterest;

  const designation = designationOverride ?? kyc?.designation ?? "";

  const submitMutation = useMutation({
    mutationFn: () =>
      companyApi.submitHrKyc({
        designation: designation.trim() || undefined,
        personal_pan: personalPan.trim().toUpperCase() || undefined,
        conflict_of_interest_accepted: conflictAccepted,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["hr-kyc"], data);
      queryClient.invalidateQueries({ queryKey: ["my-company"] });
      notifications.show({
        color: "green",
        title: HR_KYC_COPY.pageTitle,
        message: kycStatusLabel(data.kyc_status),
      });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Could not submit",
        message: err instanceof ApiError ? err.message : "Check the required fields and try again",
      });
    },
  });

  if (kycQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-240)" />;
  }

  const canSubmit = hrNeedsIdentityKyc(kyc) && designation.trim().length > 0 && conflictAccepted;
  const locked = kyc?.kyc_status === "submitted" || kyc?.kyc_status === "verified";

  return (
    <Stack gap="var(--axio-unit-32)" maw="var(--axio-unit-560)">
      <PageHeader icon={NAV_ICONS.kyc} title={HR_KYC_COPY.pageTitle} subtitle={HR_KYC_COPY.bannerBody} />

      {kyc?.kyc_bypass && (
        <Alert color="blue" title={HR_KYC_COPY.bypassTitle}>
          {HR_KYC_COPY.bypassBody}
        </Alert>
      )}
      {kyc?.kyc_status === "rejected" && (
        <Alert color="red" title={HR_KYC_COPY.bannerTitle}>
          {kyc.kyc_notes || HR_KYC_COPY.bannerBody}
        </Alert>
      )}
      {kyc?.kyc_status === "submitted" && (
        <Alert color="violet" title={kycStatusLabel(kyc.kyc_status)}>
          {HR_KYC_COPY.bannerBody}
        </Alert>
      )}

      <ContentCard>
        <Stack gap="md">
          <TextInput
            label={HR_KYC_COPY.designationLabel}
            value={designation}
            onChange={(e) => setDesignationOverride(e.currentTarget.value)}
            disabled={locked}
            required
          />
          <TextInput
            label={HR_KYC_COPY.personalPanLabel}
            value={personalPan}
            onChange={(e) => setPersonalPan(e.currentTarget.value.toUpperCase())}
            disabled={locked}
            description={kyc?.personal_pan_set ? HR_KYC_COPY.personalPanOnFile : undefined}
          />
          {hrNeedsIdentityKyc(kyc) && conflictCopy && (
            <>
              <ConflictOfInterestAck
                copy={conflictCopy}
                accepted={conflictAccepted}
                onAcceptedChange={setConflictAccepted}
              />
              <Button
                color="violet"
                loading={submitMutation.isPending}
                disabled={!canSubmit}
                onClick={() => submitMutation.mutate()}
              >
                {HR_KYC_COPY.submitCta}
              </Button>
            </>
          )}
        </Stack>
      </ContentCard>
    </Stack>
  );
}
